import React, {useState, useEffect} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Tooltip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	CircularProgress,
	Typography,
	Paper,
	Fade,
	IconButton,
	Chip,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {Check as CheckIcon, FileCopy as CopyIcon, LocalOffer as CouponIcon} from "@material-ui/icons";
import moment from "moment";
import API from "../../../../network/dashboardAPI";

const PRIMARY_COLOR = "#9cc54b";

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		borderRadius: 16,
		overflow: "hidden",
		background: "#fdfdfd",
		boxShadow: "0px 10px 40px rgba(0,0,0,0.15)",
	},
	header: {
		fontWeight: 700,
		fontSize: "1.4rem",
		letterSpacing: "0.3px",
		padding: theme.spacing(3, 3, 2),
		borderBottom: "1px solid #e0e0e0",
		background: "#fff",
	},
	content: {
		padding: theme.spacing(3),
		backgroundColor: "#fafafa",
	},
	tableWrapper: {
		borderRadius: 12,
		overflow: "hidden",
		background: "#fff",
		boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
	},
	tableHead: {
		backgroundColor: "#f5f6f5",
	},
	headCell: {
		fontWeight: 600,
		fontSize: "0.95rem",
		color: "#333",
		textTransform: "uppercase",
		borderBottom: "2px solid #e0e0e0",
	},
	tableCell: {
		fontSize: "0.95rem",
		borderBottom: "1px solid #eee",
		color: "#444",
	},
	clickableRow: {
		cursor: "pointer",
		transition: "background-color 0.2s ease",
		"&:hover": {
			backgroundColor: "#f9fdf5",
		},
	},
	selectedRow: {
		backgroundColor: "#e8f5e9",
		"&:hover": {
			backgroundColor: "#d7f0d9",
		},
	},
	applyButton: {
		backgroundColor: PRIMARY_COLOR,
		color: "#fff",
		padding: theme.spacing(1, 4),
		borderRadius: 8,
		fontWeight: 600,
		fontSize: "0.95rem",
		boxShadow: "0 3px 10px rgba(156,197,75,0.4)",
		transition: "all 0.3s ease",
		"&:hover": {
			backgroundColor: "#89ad44",
			transform: "translateY(-1px)",
			boxShadow: "0 4px 12px rgba(156,197,75,0.5)",
		},
		"&:disabled": {
			backgroundColor: "#ccc",
			color: "#888",
			boxShadow: "none",
		},
	},
	closeButton: {
		borderColor: "#ccc",
		color: "#666",
		fontWeight: 500,
		borderRadius: 8,
		padding: theme.spacing(1, 3),
		"&:hover": {
			backgroundColor: "#f9f9f9",
		},
	},
	loadingContainer: {
		textAlign: "center",
		padding: theme.spacing(6),
	},
	emptyText: {
		textAlign: "center",
		color: "#777",
		margin: theme.spacing(4, 0),
		fontSize: "1rem",
	},
	discountChip: {
		backgroundColor: "#4caf50",
		color: "#fff",
		fontWeight: 600,
	},
	actions: {
		justifyContent: "space-between",
		padding: theme.spacing(2.5, 3),
		backgroundColor: "#fff",
		borderTop: "1px solid #e0e0e0",
	},
}));

const ApplyCouponDialog = ({open, onClose, orderId, userId, onApply, orderItems, onSaveWithCoupon}) => {
	const classes = useStyles();
	const [coupons, setCoupons] = useState([]);
	const [loading, setLoading] = useState(false);
	const [applying, setApplying] = useState(false);
	const [selectedCoupon, setSelectedCoupon] = useState(null);
	const [copiedId, setCopiedId] = useState(null);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (open && userId) {
			fetchAvailableCoupons();
		}
	}, [open, userId]);

	const fetchAvailableCoupons = async () => {
		setLoading(true);
		try {
			// Use admin endpoint to get customer's coupons
			const result = await API.getAllCustomerPickCouponCode({user_id: userId});
			const response = await result.json();
			console.log('Coupon API response:', response);

			if (response?.success && response?.data) {
				setCoupons(response.data);
			} else {
				setCoupons([]);
			}
		} catch (error) {
			console.error("Error fetching coupons:", error);
			setCoupons([]);
		} finally {
			setLoading(false);
		}
	};

	const handleCopy = async (coupon) => {
		try {
			await navigator.clipboard.writeText(coupon.code);
			setCopiedId(coupon.id);
			setTimeout(() => setCopiedId(null), 1500);
		} catch (err) {
			console.error("Failed to copy code:", err);
		}
	};

	const handleRowClick = (coupon) => {
		setSelectedCoupon(selectedCoupon?.id === coupon.id ? null : coupon);
	};

	const handleApply = async () => {
		if (!selectedCoupon) return;

		setApplying(true);
		setError(null);

		try {
			// If onSaveWithCoupon is provided, use the integrated save flow
			if (onSaveWithCoupon) {
				console.log('Applying coupon and saving:', selectedCoupon.id);
				const result = await onSaveWithCoupon(selectedCoupon);

				if (result && result.success) {
					console.log('Coupon applied and saved successfully');
					handleClose();
				} else {
					setError(result?.message || 'Failed to apply coupon');
				}
			}
			// Fallback to legacy onApply callback
			else if (onApply) {
				onApply(selectedCoupon);
				handleClose();
			}
		} catch (err) {
			console.error('Error applying coupon:', err);
			setError(err.message || 'Failed to apply coupon. Please try again.');
		} finally {
			setApplying(false);
		}
	};

	const handleClose = () => {
		setSelectedCoupon(null);
		setCopiedId(null);
		setError(null);
		setApplying(false);
		onClose();
	};

	const formatDiscount = (coupon) => {
		// Check if it's a percentage discount
		if (coupon.percentage && parseFloat(coupon.percentage) > 0) {
			return `${coupon.percentage}% OFF`;
		}
		// Check if it's a fixed amount discount
		else if (coupon.fixed && parseFloat(coupon.fixed) > 0) {
			return `$${parseFloat(coupon.fixed).toFixed(2)} OFF`;
		}
		return "DISCOUNT";
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			aria-labelledby="apply-coupon-dialog"
			classes={{paper: classes.dialogPaper}}
			TransitionComponent={Fade}
			TransitionProps={{timeout: 400}}
		>
			<DialogTitle disableTypography className={classes.header}>
				<Typography variant="h6">Apply Coupon to Order</Typography>
			</DialogTitle>

			<DialogContent dividers className={classes.content}>
				{error && (
					<div style={{
						backgroundColor: '#ffebee',
						color: '#c62828',
						padding: '12px 16px',
						borderRadius: 8,
						marginBottom: 16,
						fontSize: '0.9rem'
					}}>
						{error}
					</div>
				)}

				{loading ? (
					<div className={classes.loadingContainer}>
						<CircularProgress size={30} style={{color: PRIMARY_COLOR}}/>
						<Typography
							variant="body2"
							style={{marginTop: 12, color: "#555"}}
						>
							Loading available coupons...
						</Typography>
					</div>
				) : coupons.length > 0 ? (
					<Paper elevation={0} className={classes.tableWrapper}>
						<Table>
							<TableHead className={classes.tableHead}>
								<TableRow>
									<TableCell className={classes.headCell}>Name</TableCell>
									<TableCell className={classes.headCell}>Code</TableCell>
									<TableCell className={classes.headCell}>Discount</TableCell>
									<TableCell className={classes.headCell}>Valid Until</TableCell>
									<TableCell className={classes.headCell}>Min Order</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{coupons.map((coupon, idx) => {
									const isCopied = copiedId === coupon.id;
									const isSelected = selectedCoupon?.id === coupon.id;
									return (
										<TableRow
											key={idx}
											className={`${classes.clickableRow} ${isSelected ? classes.selectedRow : ""}`}
											onClick={() => handleRowClick(coupon)}
										>
											<TableCell className={classes.tableCell}>
												<div style={{display: 'flex', alignItems: 'center', gap: 8}}>
													{isSelected && <CheckIcon fontSize="small" style={{color: PRIMARY_COLOR}}/>}
													{coupon?.name || "Unnamed Coupon"}
												</div>
											</TableCell>
											<TableCell className={classes.tableCell}>
												<div style={{display: 'flex', alignItems: 'center', gap: 8}}>
													<span>{coupon?.code || "N/A"}</span>
													<Tooltip title={isCopied ? "Copied!" : "Copy Code"} placement="top">
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation();
																handleCopy(coupon);
															}}
														>
															{isCopied ? (
																<CheckIcon fontSize="small" style={{color: PRIMARY_COLOR}}/>
															) : (
																<CopyIcon fontSize="small"/>
															)}
														</IconButton>
													</Tooltip>
												</div>
											</TableCell>
											<TableCell className={classes.tableCell}>
												<Chip
													size="small"
													label={formatDiscount(coupon)}
													className={classes.discountChip}
													icon={<CouponIcon style={{color: '#fff'}}/>}
												/>
											</TableCell>
											<TableCell className={classes.tableCell}>
												{coupon.valid_to ? moment(coupon.valid_to).format("DD/MM/YYYY") : "N/A"}
											</TableCell>
											<TableCell className={classes.tableCell}>
												{coupon.min_cart_value ? `$${parseFloat(coupon.min_cart_value).toFixed(2)}` : "No minimum"}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Paper>
				) : (
					<Typography className={classes.emptyText}>
						No coupons available for this order
					</Typography>
				)}
			</DialogContent>

			<DialogActions className={classes.actions}>
				<Button
					className={classes.closeButton}
					variant="outlined"
					onClick={handleClose}
					disabled={applying}
				>
					Cancel
				</Button>
				<Button
					className={classes.applyButton}
					variant="contained"
					onClick={handleApply}
					disabled={!selectedCoupon || applying}
					startIcon={applying ? <CircularProgress size={16} style={{color: 'white'}}/> : null}
				>
					{applying ? 'Applying...' : 'Apply Coupon'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ApplyCouponDialog;
